import { Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGeo } from "@/context/GeoContext";
import { buildAffiliateHref } from "@/lib/affiliate";
import { resolveDisplay, formatPrice } from "@/lib/currency";

export default function PackageOptionCard({ packet }) {
    const { geo, currencyPref } = useGeo();
    const { t } = useTranslation();
    const region = geo?.affiliate_region || "WORLD";
    const {
        id,
        title,
        name,
        destination,
        duration_days,
        duration_nights,
        vibe,
        price,
        display_price,
        image_url,
        highlights = [],
        book_url,
        is_grounded,
        source_label,
        confidence,
    } = packet;

    const { display, original, sameCurrency } = resolveDisplay(
        price,
        display_price,
        currencyPref,
    );

    return (
        <article
            className="relative w-[300px] shrink-0 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white transition hover:border-[#0A0A0A]"
            data-testid={`package-option-${id}`}
        >
            {/* Purple dot top-right (Packages) */}
            <span
                aria-hidden
                className="absolute right-3 top-3 z-10 inline-block h-2.5 w-2.5 rounded-full bg-[#7C3AED] ring-4 ring-[#7C3AED]/15"
                data-testid="card-color-dot"
            />

            <div className="aspect-[16/9] w-full overflow-hidden bg-[#F5F5F5]">
                {image_url ? (
                    <img
                        src={image_url}
                        alt={title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                    />
                ) : null}
            </div>

            <div className="p-4">
                <span className="inline-block rounded-full border border-[#7C3AED]/30 bg-[#7C3AED]/5 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[#7C3AED]">
                    {vibe}
                </span>
                <h3 className="mt-2 line-clamp-1 text-[15px] font-semibold tracking-tight text-[#0A0A0A]">
                    {title || name}
                </h3>
                <div className="mt-1 flex items-center gap-2 text-[12px] text-[#737373]">
                    <span>{destination}</span>
                    <span className="text-[#A3A3A3]">·</span>
                    <span className="inline-flex items-center gap-1">
                        <Calendar size={11} strokeWidth={2} /> {duration_days || duration_nights} {duration_days ? "days" : "nights"}
                    </span>
                </div>

                {highlights.length > 0 && (
                    <ul className="mt-3 space-y-1">
                        {highlights.slice(0, 3).map((h, i) => (
                            <li
                                key={i}
                                className="line-clamp-1 text-[12px] text-[#0A0A0A]"
                            >
                                <span className="mr-1.5 inline-block h-1 w-1 rounded-full bg-[#7C3AED] align-middle" />
                                {h}
                            </li>
                        ))}
                    </ul>
                )}

                <div className="mt-4 flex items-end justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                            from
                        </div>
                        <div
                            className="text-[18px] font-semibold leading-none tracking-tight text-[#0A0A0A]"
                            data-testid="package-price"
                        >
                            {formatPrice(display)}
                        </div>
                        {!sameCurrency && (currencyPref || "USD").toUpperCase() === "USD" && (
                            <div
                                className="mt-0.5 text-[10px] text-[#A3A3A3]"
                                data-testid="package-price-original"
                            >
                                {formatPrice(original)} {original.currency}
                            </div>
                        )}
                        {is_grounded && confidence !== "high" && (
                            <div
                                className="mt-0.5 text-[10px] italic text-[#A3A3A3]"
                                data-testid="package-price-approx"
                            >
                                approx
                            </div>
                        )}
                    </div>
                    {!!book_url && (
                        <a
                            href={is_grounded ? book_url : buildAffiliateHref(book_url, region)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`book-now-${id}`}
                            className="inline-flex items-center justify-center rounded-lg bg-[#0A0A0A] px-4 py-2 text-[12px] font-medium text-white transition hover:bg-[#262626]"
                        >
                            {is_grounded ? t("cards.view_on", { source: source_label || "Web" }) : t("cards.book_now")}
                        </a>
                    )}
                </div>
            </div>
        </article>
    );
}
